import { v4 as uuidv4 } from 'uuid'

import { normalizeDashboards } from '../../../../utils'
import { ensureUniqueObjectIds } from '@edge/spark/utils'
import { history } from '@edge/spark/utils'
import { getActiveDashboardId, setActiveDashboardId } from '../../../../Stores'
import {
    UserAuthenticationIdDocument as userIdQuery,
    type UserAuthenticationIdQuery,
    ClientUserDashboardsDocument as clientUserDashboardsQuery,
    type ClientUserDashboardsQuery,
    type MutationResolvers,
    UpdateUserDashboardsDocument as mutation,
    type UpdateUserDashboardsMutation,
} from '../../../../generated/graphql'
import { createDialog } from '@edge/spark/feature-dialog'
import type { ApolloResolverContext } from '@edge/spark/io-apollo'
import { getActiveAuctionId } from '@edge/spark/io-roles'

const copyDashboardCard: MutationResolvers<ApolloResolverContext>['copyDashboardCard'] = async (
    _root,
    {
        widgetId,
        heading,
        priorDashId: customPriorDashId,
        priorUserId: customPriorUserId,
        dashId: customTargetDashId,
        userId: customTargetUserId,
    },
    { cache, client },
) => {
    console.debug('io-dashboards copyDashboardCard Deprecated, refactor (soon)')

    if (!client) {
        throw new Error('Missing ApolloClient')
    }

    const auctionId = getActiveAuctionId()

    if (!auctionId) {
        throw new Error('copyDashboardCard: Missing AuctionId')
    }

    if (customTargetUserId && !customTargetDashId) {
        throw new Error('Custom target userId without dashId')
    }

    if (customPriorUserId && !customPriorDashId) {
        throw new Error('Custom priorUserId without priorDashId')
    }

    const { data: userIdData } = await client.query<UserAuthenticationIdQuery>({
        query: userIdQuery,
    })

    const { data: targetUserDashboardsData } = await client.query<ClientUserDashboardsQuery>({
        query: clientUserDashboardsQuery,
        variables: { userId: customTargetUserId },
        fetchPolicy: 'network-only',
    })

    const { data: priorUserDashboardsData } = await client.query<ClientUserDashboardsQuery>({
        query: clientUserDashboardsQuery,
        variables: { userId: customPriorUserId },
        fetchPolicy: 'network-only',
    })

    const activeDashboardId = getActiveDashboardId()

    const targetDashId = customTargetDashId || activeDashboardId
    const targetDashboards = normalizeDashboards(targetUserDashboardsData?.dashboards)
    const targetDashboardIndex = targetDashboards.findIndex((d) => d.id === targetDashId)
    const targetDashboard = targetDashboards[targetDashboardIndex]

    if (!targetDashboard) {
        createDialog({
            action: 'OK',
            message: `Error: Target dashboard with id:${targetDashId} not found`,
            timeout: 5000,
        })

        return null
    }

    const priorDashId = customPriorDashId || activeDashboardId
    const priorDashboards = normalizeDashboards(priorUserDashboardsData?.dashboards)
    const priorDashboardIndex = priorDashboards.findIndex((d) => d.id === priorDashId)
    const priorDashboard = priorDashboards[priorDashboardIndex]

    if (!targetDashboard) {
        createDialog({
            action: 'OK',
            message: `Error: Prior dashboard with id:${priorDashId} not found`,
            timeout: 5000,
        })

        return null
    }

    const priorCard = priorDashboard.cards.find((c) => c.id === widgetId)

    if (!priorCard) {
        createDialog({
            action: 'OK',
            message: `Error: Prior card with id:${widgetId} not found on dashId:${priorDashId} `,
            timeout: 5000,
        })

        return null
    }

    const cardDefWithHeading: typeof priorCard = heading
        ? {
              ...priorCard,
              config: {
                  ...priorCard.config,
                  title: heading,
              },
          }
        : priorCard

    const cardId = uuidv4()
    const cardWithUUID: typeof priorCard = { ...cardDefWithHeading, id: cardId }
    const concatenatedCards = targetDashboard.cards.concat(cardWithUUID)
    const cleanedCards = ensureUniqueObjectIds(concatenatedCards)
    const updatedDashboard = { ...targetDashboard, cards: cleanedCards }
    const updatedDashboards = [
        ...targetDashboards.slice(0, targetDashboardIndex),
        updatedDashboard,
        ...targetDashboards.slice(targetDashboardIndex + 1),
    ]
    const cleanedDashboards = ensureUniqueObjectIds(updatedDashboards)
    const stringifiedUpdatedDashboards = cleanedDashboards.map((d) => JSON.stringify(d))
    const isTargetUserSelf = !customTargetUserId || customTargetUserId === userIdData.user?.attributes?.authenticationId
    const admin = isTargetUserSelf ? undefined : { auctionId, subjectUserId: customTargetUserId }
    const title = cardDefWithHeading.config?.title || ''

    await client.mutate<UpdateUserDashboardsMutation>({
        mutation,
        variables: { admin, dashboards: stringifiedUpdatedDashboards },
        optimisticResponse: {
            updateUser: {
                __typename: 'UserMutationResponse',
                authenticationId: customTargetUserId || null,
                statusCode: 200,
                statusMessage: 'Updated dashboards.',
            },
        },

        update: (store): void => {
            if (isTargetUserSelf) {
                setActiveDashboardId(targetDashId)
                history.push('/')
            }
            if (customTargetUserId) {
                store.writeData({
                    data: {
                        [`dashboards({"userId":"${customTargetUserId}"})`]: stringifiedUpdatedDashboards,
                        administrator: {
                            __typename: 'Administrator',
                            [`user({"input":{"admin":{"auctionId":"${auctionId}","subjectUserId":"${customTargetUserId}"}}})`]:
                                {
                                    __typename: 'User',
                                    dashboards: stringifiedUpdatedDashboards,
                                },
                        },
                    },
                })
            }

            if (isTargetUserSelf) {
                store.writeData({
                    data: {
                        'dashboards({})': stringifiedUpdatedDashboards,
                        user: {
                            __typename: 'User',
                            dashboards: stringifiedUpdatedDashboards,
                        },
                    },
                })
            }
        },
    })

    createDialog({
        message: `Copied${title ? ` '${title}'` : ''} widget`,
        timeout: 5000,
    })

    return true
}

export default copyDashboardCard
